import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Divider,
  Typography,
} from "@mui/material";
import { EntryTimingGradeRow } from "portal/components/EntryTimingGradeRow";
import {
  getMetricName,
  METRIC_AVERAGE_WEED_SIZE_METER,
  METRIC_KILLED_WEEDS,
  METRIC_MISSED_WEEDS,
  METRIC_SKIPPED_WEEDS,
  METRIC_TOTAL_WEEDS,
  METRIC_TOTAL_WEEDS_IN_BAND,
  METRIC_WEED_DENSITY_SQ_METER,
} from "portal/utils/certifiedMetrics";
import { JigsawCropMetrics } from "./JigsawCropMetrics";
import { JigsawWeedFractions } from "./JigsawWeedFractions";
import { Measurement } from "portal/components/measurement/Measurement";
import { MetricHelp } from "portal/components/MetricHelp";
import { MetricNumber } from "portal/utils/metrics";
import { MetricRow, MetricSubsection } from "./MetricSubsection";
import { PlantProfileMetrics } from "protos/portal/metrics";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { FunctionComponent } from "react";

/**
 * One collapsible full-width group of the estimated field metrics: the weed
 * and crop metrics observed under one plant profile (or with none active, or
 * unattributable to any). The layout inside mirrors the ungrouped view's two
 * columns, scoped to the group.
 */
export const PlantProfileMetricsSection: FunctionComponent<{
  group: PlantProfileMetrics;
  // Jobs grade each profile. A calendar day does not.
  showEntryTiming: boolean;
}> = ({ group, showEntryTiming }) => {
  const { t } = useTranslation();
  const isProfile = group.profileId !== "";

  const title = isProfile
    ? group.profileName
    : group.unattributed
    ? t("views.metrics.sections.unknownPlantProfile")
    : t("views.metrics.sections.noPlantProfile");

  return (
    <Accordion
      defaultExpanded
      disableGutters
      variant="outlined"
      className="w-full bg-transparent before:hidden rounded-[20px]"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <div className="flex items-center gap-3">
          <Typography className="font-bold">{title}</Typography>
          {isProfile && (
            <Chip label={t("views.metrics.plantProfileChip")} size="small" />
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-3">
          <div className="flex flex-col gap-3 lg:pr-12">
            <GroupMetricRow
              metric={METRIC_WEED_DENSITY_SQ_METER}
              value={group.weedDensityPerSquareMeter || undefined}
            />
            <GroupMetricRow
              metric={METRIC_AVERAGE_WEED_SIZE_METER}
              value={group.averageWeedRadiusMeters || undefined}
            />
            {showEntryTiming ? (
              <EntryTimingGradeRow grade={group.entryTimingGrade} />
            ) : undefined}
            <MetricSubsection>
              <GroupMetricRow
                metric={METRIC_TOTAL_WEEDS}
                value={group.totalWeeds}
              />
              <GroupMetricRow
                metric={METRIC_TOTAL_WEEDS_IN_BAND}
                value={group.totalWeedsInBand}
              />
              <Divider className="w-full my-2" />
              <GroupMetricRow
                metric={METRIC_KILLED_WEEDS}
                value={group.killedWeeds}
              />
              <GroupMetricRow
                metric={METRIC_MISSED_WEEDS}
                value={group.missedWeeds}
              />
              <GroupMetricRow
                metric={METRIC_SKIPPED_WEEDS}
                value={group.skippedWeeds}
              />
            </MetricSubsection>
            <JigsawWeedFractions
              weedMetricsByCategory={group.weedMetricsByCategory}
            />
          </div>
          <div className="flex flex-col gap-3 lg:pr-12">
            <JigsawCropMetrics
              cropMetricsByCategory={group.cropMetricsByCategory}
            />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

// Like InlineMetric, but takes the group-scoped value directly instead of
// looking it up on the response by metric id.
const GroupMetricRow: FunctionComponent<{
  metric: MetricNumber;
  value: number | undefined;
}> = ({ metric, value }) => {
  const { t } = useTranslation();

  return (
    <MetricRow>
      <span className="font-medium">
        {getMetricName(t, metric)}
        <MetricHelp metric={metric} className="ms-1 align-middle" />
      </span>
      <Measurement
        cycleSlot={metric.cycleSlot}
        unit={metric.units}
        value={value}
      />
    </MetricRow>
  );
};
